import DOMPurify from 'isomorphic-dompurify';

const ALLOWED_TAGS = [
  'a',
  'b',
  'br',
  'em',
  'i',
  'mark',
  'p',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'u',
];

const ALLOWED_ATTR = ['href', 'title', 'target', 'rel', 'class'];

/**
 * Hosts whose iframes are allowed to remain in sanitized HTML.
 * Anything else is stripped. Keeps SEC-021 / SEC-032 compliance:
 *  - Sandboxed when not from a trusted media provider.
 *  - referrerpolicy=no-referrer added by default.
 */
const TRUSTED_IFRAME_HOSTS = [
  'www.youtube.com',
  'youtube.com',
  'youtu.be',
  'www.youtube-nocookie.com',
  'player.vimeo.com',
  'vimeo.com',
  'www.loom.com',
  'loom.com',
  'platform.twitter.com',
];

let purifyConfigured = false;

function configurePurify(): void {
  if (purifyConfigured) return;
  purifyConfigured = true;

  DOMPurify.addHook('uponSanitizeElement', (node, data) => {
    if (data.tagName !== 'iframe') return;
    const el = node as HTMLIFrameElement;
    const src = el.getAttribute('src') ?? '';
    let host: string | null = null;
    try {
      host = new URL(src).host;
    } catch {
      host = null;
    }
    if (!host || !TRUSTED_IFRAME_HOSTS.includes(host)) {
      // Strip untrusted iframes entirely.
      el.remove();
      return;
    }
    // Harden trusted iframes.
    el.setAttribute('referrerpolicy', 'no-referrer');
    el.setAttribute('loading', 'lazy');
    if (!el.hasAttribute('allow')) {
      el.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; picture-in-picture');
    }
  });
}

export interface SanitizeOptions {
  allowedTags?: string[];
  allowedAttr?: string[];
}

export function sanitizeRichText(dirty: string | undefined | null, opts: SanitizeOptions = {}): string {
  if (!dirty) return '';
  configurePurify();
  return DOMPurify.sanitize(String(dirty), {
    ALLOWED_TAGS: opts.allowedTags ?? ALLOWED_TAGS,
    ALLOWED_ATTR: opts.allowedAttr ?? ALLOWED_ATTR,
    ALLOW_DATA_ATTR: true,
    ADD_TAGS: ['iframe'],
    ADD_ATTR: ['allow', 'allowfullscreen', 'frameborder', 'referrerpolicy', 'loading'],
    KEEP_CONTENT: true,
  });
}

export function sanitizedHtmlProps(html: string | undefined | null, opts?: SanitizeOptions) {
  return { __html: sanitizeRichText(html, opts) };
}
